import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  LuArrowRight,
  LuBookOpen,
  LuBuilding2,
  LuCalendarDays,
  LuChevronLeft,
  LuChevronRight,
  LuClipboardList,
  LuFileText,
  LuHeart,
  LuLayoutDashboard,
  LuMail,
  LuMessageCircle,
  LuPackage,
  LuRotateCcw,
  LuSearch,
  LuShoppingCart,
  LuTicket,
  LuTruck,
  LuUser,
  LuWallet,
  LuWrench,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HelpSupportIllustration from "../../components/help/HelpSupportIllustration";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/help_center.scss";
import "../../assets/styles/help_articles.scss";

const PAGE_SIZE = 12;

const FILTERS = [
  { id: "all", label: "All", icon: LuFileText, tone: "blue" },
  { id: "buyer", label: "Buyer", icon: LuShoppingCart, tone: "blue" },
  { id: "seller", label: "Seller", icon: LuUser, tone: "green" },
  { id: "orders", label: "Orders", icon: LuClipboardList, tone: "orange" },
  { id: "payments", label: "Payments", icon: LuWallet, tone: "purple" },
  { id: "logistics", label: "Logistics", icon: LuTruck, tone: "cyan" },
  { id: "manufacturing", label: "Manufacturing", icon: LuBuilding2, tone: "navy" },
  { id: "technical", label: "Technical Support", icon: LuWrench, tone: "red" },
];

const CATEGORY_LABELS = {
  buyer: "Buyer",
  seller: "Seller",
  orders: "Orders",
  payments: "Payments",
  logistics: "Logistics",
  manufacturing: "Manufacturing",
  technical: "Technical",
};

const ALL_ARTICLES = [
  { id: "create-rfq", title: "How to Create RFQ", description: "Learn how to post a sourcing request and invite suppliers.", category: "buyer", updated: "10 Jul 2026", time: "3 min read", icon: LuFileText, tone: "blue" },
  { id: "compare-quotes", title: "Compare Quotations", description: "Review and compare quotes from multiple suppliers.", category: "buyer", updated: "08 Jul 2026", time: "4 min read", icon: LuWallet, tone: "green" },
  { id: "manage-po", title: "Manage Purchase Orders", description: "Create, manage and track your purchase orders.", category: "buyer", updated: "06 Jul 2026", time: "4 min read", icon: LuClipboardList, tone: "orange" },
  { id: "upload-products", title: "Upload Products", description: "Add product listings with images, specs and pricing.", category: "seller", updated: "09 Jul 2026", time: "4 min read", icon: LuPackage, tone: "green" },
  { id: "verify-seller", title: "Verify Seller Account", description: "Complete KYC and unlock seller features.", category: "seller", updated: "06 Jul 2026", time: "4 min read", icon: LuUser, tone: "green" },
  { id: "track-orders", title: "Track Orders", description: "Track your order status from processing to delivery.", category: "orders", updated: "06 Jul 2026", time: "3 min read", icon: LuTruck, tone: "purple" },
  { id: "payment-methods", title: "Payment Methods", description: "Learn about supported payment options on Trocanet.", category: "payments", updated: "07 Jul 2026", time: "3 min read", icon: LuWallet, tone: "purple" },
  { id: "payment-issues", title: "Payment Issues", description: "Resolve failed payments and invoice problems.", category: "payments", updated: "07 Jul 2026", time: "4 min read", icon: LuWallet, tone: "purple" },
  { id: "seller-register", title: "Register as a Seller", description: "Create your seller account and company profile.", category: "seller", updated: "10 Jul 2026", time: "3 min read", icon: LuUser, tone: "green" },
  { id: "track-shipment", title: "Track Shipment", description: "Monitor pickup, transit and delivery updates.", category: "logistics", updated: "05 Jul 2026", time: "3 min read", icon: LuTruck, tone: "cyan" },
  { id: "cm-rfq", title: "Send Manufacturing RFQ", description: "Request OEM/ODM production from verified partners.", category: "manufacturing", updated: "09 Jul 2026", time: "4 min read", icon: LuBuilding2, tone: "navy" },
  { id: "login-issues", title: "Login Issues", description: "Fix sign-in problems and reset your access.", category: "technical", updated: "08 Jul 2026", time: "3 min read", icon: LuWrench, tone: "red" },
  { id: "buyer-dashboard", title: "Buyer Dashboard Guide", description: "Overview of buyer dashboard features and tools.", category: "buyer", updated: "03 Jul 2026", time: "2 min read", icon: LuLayoutDashboard, tone: "cyan" },
  { id: "favorite-suppliers", title: "Favorite Suppliers", description: "Save and manage your favorite suppliers easily.", category: "buyer", updated: "02 Jul 2026", time: "2 min read", icon: LuHeart, tone: "yellow" },
  { id: "returns-refunds", title: "Returns & Refunds", description: "Learn about return requests, refunds and replacement.", category: "buyer", updated: "04 Jul 2026", time: "3 min read", icon: LuRotateCcw, tone: "red" },
  { id: "buyer-account", title: "Buyer Account Settings", description: "Update profile, users and notification preferences.", category: "buyer", updated: "01 Jul 2026", time: "3 min read", icon: LuUser, tone: "blue" },
  { id: "seller-catalog", title: "Manage Product Catalog", description: "Organize listings, categories and inventory.", category: "seller", updated: "05 Jul 2026", time: "4 min read", icon: LuPackage, tone: "green" },
  { id: "seller-pricing", title: "Pricing & Discounts", description: "Set pricing rules and promotional offers.", category: "seller", updated: "04 Jul 2026", time: "3 min read", icon: LuWallet, tone: "orange" },
  { id: "order-cancellation", title: "Cancel an Order", description: "Steps to cancel orders before dispatch.", category: "orders", updated: "03 Jul 2026", time: "3 min read", icon: LuClipboardList, tone: "orange" },
  { id: "invoice-download", title: "Download Invoice", description: "Find and download billing documents.", category: "orders", updated: "02 Jul 2026", time: "2 min read", icon: LuFileText, tone: "purple" },
  { id: "payment-failed", title: "Failed Payment Recovery", description: "Resolve failed payments and retry transactions.", category: "payments", updated: "01 Jul 2026", time: "3 min read", icon: LuWallet, tone: "purple" },
  { id: "refund-status", title: "Refund Status", description: "Track refund processing and settlement timelines.", category: "payments", updated: "30 Jun 2026", time: "3 min read", icon: LuRotateCcw, tone: "purple" },
  { id: "book-shipment", title: "Book a Shipment", description: "Request freight quotes from logistics providers.", category: "logistics", updated: "29 Jun 2026", time: "4 min read", icon: LuClipboardList, tone: "cyan" },
  { id: "delivery-issues", title: "Delivery Issues", description: "Report delayed or damaged deliveries.", category: "logistics", updated: "28 Jun 2026", time: "3 min read", icon: LuTruck, tone: "cyan" },
  { id: "cm-production", title: "Track Production", description: "Follow manufacturing milestones and deliveries.", category: "manufacturing", updated: "27 Jun 2026", time: "3 min read", icon: LuBuilding2, tone: "navy" },
  { id: "cm-quality", title: "Quality Inspection", description: "Request QC checks during manufacturing.", category: "manufacturing", updated: "26 Jun 2026", time: "4 min read", icon: LuFileText, tone: "navy" },
  { id: "report-bug", title: "Report a Platform Issue", description: "Raise a ticket for errors or unexpected behavior.", category: "technical", updated: "25 Jun 2026", time: "3 min read", icon: LuWrench, tone: "red" },
  { id: "browser-support", title: "Browser Compatibility", description: "Supported browsers and troubleshooting tips.", category: "technical", updated: "24 Jun 2026", time: "2 min read", icon: LuWrench, tone: "red" },
  { id: "rfq-templates", title: "RFQ Templates", description: "Use templates to post RFQs faster.", category: "buyer", updated: "23 Jun 2026", time: "3 min read", icon: LuClipboardList, tone: "blue" },
  { id: "supplier-search", title: "Find Verified Suppliers", description: "Search and filter verified supplier profiles.", category: "buyer", updated: "22 Jun 2026", time: "3 min read", icon: LuSearch, tone: "blue" },
  { id: "seller-leads", title: "Manage Buyer Leads", description: "Respond to enquiries and RFQ invitations.", category: "seller", updated: "21 Jun 2026", time: "4 min read", icon: LuMessageCircle, tone: "green" },
  { id: "seller-analytics", title: "Seller Analytics", description: "View performance metrics and listing insights.", category: "seller", updated: "20 Jun 2026", time: "3 min read", icon: LuLayoutDashboard, tone: "green" },
  { id: "po-approval", title: "PO Approval Workflow", description: "Configure purchase order approval rules.", category: "orders", updated: "19 Jun 2026", time: "4 min read", icon: LuClipboardList, tone: "orange" },
  { id: "grn-process", title: "Goods Receipt (GRN)", description: "Confirm received goods against purchase orders.", category: "orders", updated: "18 Jun 2026", time: "3 min read", icon: LuPackage, tone: "orange" },
  { id: "escrow-payments", title: "Escrow Payments", description: "How escrow protects buyers and sellers.", category: "payments", updated: "17 Jun 2026", time: "4 min read", icon: LuWallet, tone: "purple" },
  { id: "tax-invoices", title: "Tax Invoices & GST", description: "GST-compliant invoices and tax documentation.", category: "payments", updated: "16 Jun 2026", time: "3 min read", icon: LuFileText, tone: "purple" },
  { id: "freight-rates", title: "Freight Rate Comparison", description: "Compare logistics quotes before booking.", category: "logistics", updated: "15 Jun 2026", time: "3 min read", icon: LuTruck, tone: "cyan" },
  { id: "customs-docs", title: "Customs Documentation", description: "Export/import documents for international shipments.", category: "logistics", updated: "14 Jun 2026", time: "4 min read", icon: LuFileText, tone: "cyan" },
  { id: "cm-agreements", title: "Manufacturing Agreements", description: "Review and sign production agreements.", category: "manufacturing", updated: "13 Jun 2026", time: "4 min read", icon: LuBuilding2, tone: "navy" },
  { id: "cm-samples", title: "Request Product Samples", description: "Order samples before bulk production.", category: "manufacturing", updated: "12 Jun 2026", time: "3 min read", icon: LuPackage, tone: "navy" },
  { id: "two-factor", title: "Two-Factor Authentication", description: "Enable 2FA for enhanced account security.", category: "technical", updated: "11 Jun 2026", time: "3 min read", icon: LuUser, tone: "red" },
  { id: "api-access", title: "API Access & Integration", description: "Connect Trocanet with your business systems.", category: "technical", updated: "10 Jun 2026", time: "5 min read", icon: LuWrench, tone: "red" },
  { id: "notifications", title: "Notification Settings", description: "Customize email and in-app notifications.", category: "buyer", updated: "09 Jun 2026", time: "2 min read", icon: LuMail, tone: "blue" },
  { id: "dispute-guide", title: "Submit a Dispute", description: "How to raise and track platform disputes.", category: "buyer", updated: "08 Jun 2026", time: "3 min read", icon: LuFileText, tone: "blue" },
];

function parseDate(value) {
  const parsed = Date.parse(String(value).replace(/(\d{2}) (\w{3}) (\d{4})/, "$2 $1, $3"));
  return Number.isNaN(parsed) ? 0 : parsed;
}

export default function HelpCenterArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = String(searchParams.get("q") || "");
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    let items = ALL_ARTICLES.filter((article) => {
      const categoryMatch = category === "all" || article.category === category;
      const queryMatch =
        !normalizedQuery ||
        `${article.title} ${article.description} ${CATEGORY_LABELS[article.category]}`
          .toLowerCase()
          .includes(normalizedQuery);
      return categoryMatch && queryMatch;
    });

    items = [...items].sort((a, b) => {
      if (sort === "oldest") return parseDate(a.updated) - parseDate(b.updated);
      if (sort === "title") return a.title.localeCompare(b.title);
      return parseDate(b.updated) - parseDate(a.updated);
    });

    return items;
  }, [category, query, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageArticles = filtered.slice(start, start + PAGE_SIZE);
  const end = Math.min(start + PAGE_SIZE, filtered.length);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 4, "ellipsis", totalPages];
    if (currentPage >= totalPages - 2) {
      return [1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
  }, [currentPage, totalPages]);

  const onSearchChange = (value) => {
    setQuery(value);
    setPage(1);
    const next = value.trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <div className="help-center-page help-articles-page">
      <B2BHubHomeHeader />
      <main className="help-articles">
        <nav className="help-articles__breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">All Articles</span>
        </nav>

        <header className="help-articles__head">
          <div className="help-articles__title">
            <span className="help-articles__title-icon" aria-hidden>
              <LuBookOpen />
            </span>
            <div>
              <h1>All Articles</h1>
              <p>Browse guides, tutorials and FAQs across the Trocanet platform.</p>
            </div>
          </div>
          <div className="help-articles__stats">
            <div>
              <span className="help-articles__stat-icon help-articles__stat-icon--blue">
                <LuFileText aria-hidden />
              </span>
              <strong>{ALL_ARTICLES.length}</strong>
              <small>Articles</small>
            </div>
            <div>
              <span className="help-articles__stat-icon help-articles__stat-icon--green">
                <LuLayoutDashboard aria-hidden />
              </span>
              <strong>8</strong>
              <small>Categories</small>
            </div>
            <div>
              <span className="help-articles__stat-icon help-articles__stat-icon--purple">
                <LuCalendarDays aria-hidden />
              </span>
              <strong>Updated</strong>
              <small>Weekly</small>
            </div>
          </div>
        </header>

        <section className="help-articles__toolbar">
          <label className="help-articles__search">
            <input
              type="search"
              value={query}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search articles by keyword..."
            />
            <LuSearch aria-hidden />
          </label>
          <div className="help-articles__filters">
            {FILTERS.map((filter) => {
              const Icon = filter.icon;
              const active = category === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  className={active ? "is-active" : ""}
                  onClick={() => {
                    setCategory(filter.id);
                    setPage(1);
                  }}
                >
                  {filter.id !== "all" ? (
                    <span className={`help-articles__filter-icon help-articles-tone--${filter.tone}`}>
                      <Icon aria-hidden />
                    </span>
                  ) : null}
                  {filter.label}
                </button>
              );
            })}
          </div>
          <label className="help-articles__sort">
            <span>Sort by:</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">Title A–Z</option>
            </select>
          </label>
        </section>

        <p className="help-articles__count">
          {filtered.length
            ? `Showing ${start + 1} – ${end} of ${filtered.length} Articles`
            : "Showing 0 Articles"}
        </p>

        {pageArticles.length ? (
          <div className="help-articles__grid">
            {pageArticles.map((article) => {
              const Icon = article.icon;
              return (
                <article key={article.id} className="help-articles__card">
                  <span className={`help-articles__badge help-articles-tone--${article.tone}`}>
                    {CATEGORY_LABELS[article.category]}
                  </span>
                  <div className="help-articles__card-main">
                    <span className={`help-articles__card-icon help-articles-tone--${article.tone}`} aria-hidden>
                      <Icon />
                    </span>
                    <div>
                      <h2>{article.title}</h2>
                      <p>{article.description}</p>
                    </div>
                  </div>
                  <div className="help-articles__card-foot">
                    <span>
                      {article.time} • Updated {article.updated}
                    </span>
                    <Link to={`/help-center/articles/${article.id}`}>
                      Read Article <LuArrowRight aria-hidden />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="help-articles__empty">No articles matched your search or filter.</div>
        )}

        {totalPages > 1 ? (
          <nav className="help-articles__pagination" aria-label="Articles pagination">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden /> Previous
            </button>
            {pageNumbers.map((item, index) =>
              item === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="help-articles__ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={currentPage === item ? "is-active" : ""}
                  onClick={() => setPage(item)}
                >
                  {item}
                </button>
              ),
            )}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              Next <LuChevronRight aria-hidden />
            </button>
          </nav>
        ) : null}

        <section className="help-articles__cta">
          <div className="help-articles__cta-visual">
            <HelpSupportIllustration />
          </div>
          <div className="help-articles__cta-copy">
            <h2>Still need help?</h2>
            <p>Our support team is here to assist you.</p>
          </div>
          <div className="help-articles__cta-actions">
            <Link to="/help-center/raise-ticket" className="help-articles__cta-primary">
              <LuTicket aria-hidden /> Raise Ticket
            </Link>
            <Link to="/contact?enquiry=general">
              <LuMessageCircle aria-hidden /> Live Chat
            </Link>
            <a href="mailto:support@trocanet.com">
              <LuMail aria-hidden /> Email Support
            </a>
          </div>
        </section>
      </main>
      <HubHomeFooter />
    </div>
  );
}
